import { makeNamespacedLog } from '@sb/log';
import type { ArmJointPositions, ArmTarget } from '@sb/motion-planning';
import type { RoutineContext } from '@sb/routine-runner/RoutineContext';
import type { StatefulEventEmitter } from '@sb/utilities';

/**
 * This class saves the joint angles whenever there is a pause event on the
 * event emitter.  When getUpdatedTargets is called, it will add the saved position
 * as the first point to the target list.  This is to solve the issue when where
 * the arm resumes from a position that is not the last original motion plan.
 * With this change, the arm will go back to the last position before the pause
 * then continue with the original motion plan.
 */

const log = makeNamespacedLog('PausedStatePositionSaver');

export class PausedStatePositionSaver {
  private events: StatefulEventEmitter<any>;

  private jointAngles: ArmJointPositions | undefined;

  private routineContext: RoutineContext;

  private pauseListener: () => void | undefined;

  constructor({
    events,
    routineContext,
  }: {
    events: StatefulEventEmitter<any>;
    routineContext: RoutineContext;
  }) {
    this.events = events;
    this.routineContext = routineContext;

    this.pauseListener = this.events.on('pause', () => {
      log.info(`pause`, 'Paused, setting saved position', {
        jointAngles:
          this.routineContext.getRoutineRunnerState().kinematicState
            .jointAngles,
      });

      this.jointAngles = [
        ...this.routineContext.getRoutineRunnerState().kinematicState
          .jointAngles,
      ];
    });
  }

  public destroy() {
    this.pauseListener();
  }

  getUpdatedTargets(targets: ArmTarget[]): ArmTarget[] {
    if (!this.jointAngles) {
      return targets;
    }

    log.info(
      `getUpdatedTargets`,
      'getUpdatedTargets, resuming from last joint angles',
      {
        lastJointAngles: this.jointAngles,
        currentJointAngles:
          this.routineContext.getRoutineRunnerState().kinematicState
            .jointAngles,
      },
    );

    const pausedTarget: ArmTarget = {
      motionKind: 'joint',
      jointAngles: this.jointAngles,
    };

    return [pausedTarget, ...targets];
  }
}
